import { useAtom } from 'jotai';
import { useEffect, type FunctionComponent } from 'react';
import { Route, Routes } from 'react-router-dom';
import { sridsWithUserReloadAtom } from './atoms';
import { getOrgSlugFromHostname } from './core/domain';
import Layout from './layout/Layout';
import AccountPage from './pages/AccountPage';
import ActivatePage from './pages/auth/ActivatePage';
import CallbackPage from './pages/auth/CallbackPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import EmailVerifiedPage from './pages/auth/EmailVerifiedPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import InvitationPage from './pages/auth/InvitationPage';
import LoginPage from './pages/auth/LoginPage';
import LogoutPage from './pages/auth/LogoutPage';
import OnboardingInvitePage from './pages/auth/OnboardingInvitePage';
import OnboardingOrgPage from './pages/auth/OnboardingOrgPage';
import OnboardingSurveyPage from './pages/auth/OnboardingSurveyPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SignupPage from './pages/auth/SignupPage';
import VerifySignupPage from './pages/auth/VerifySignupPage';
import ArchivedProjectsPage from './pages/ArchivedProjects';
import Dashboard from './pages/Dashboard';
import NotFoundPage from './pages/NotFoundPage';
import OrganizationSettings from './pages/OrganizationSettings';
import OrganizationsLoaderPage from './pages/OrganizationsLoader';
import OrganizationUsers from './pages/OrganizationUsers';
import { AttributeTable } from './pages/Project/AttributeTable';
import BatchPage from './pages/Project/Batch';
import { AnalysesPage } from './pages/Project/Analyses/AnalysesPage';
import ProjectEditPage from './pages/Project/ProjectEdit';
import ProjectLayersPage from './pages/Project/ProjectLayers';
import ProjectUploadPage from './pages/Project/ProjectUpload';
import ProjectWrapper from './pages/Project/ProjectWrapper';
import ReferralsPage from './pages/ReferralsPage';
import SharedPage from './pages/SharedPage';
import SubscriptionPage from './pages/SubscriptionPage';

interface iRouterProps {}

const Router: FunctionComponent<iRouterProps> = () => {
  const [srids, loadSrids] = useAtom(sridsWithUserReloadAtom);
  const isOrgDomain = !!getOrgSlugFromHostname();

  useEffect(() => {
    // Load srids when the app starts
    loadSrids();
  }, [srids.user?.id, loadSrids]);
  return (
    <Routes>
      <Route path='/auth/login' element={<LoginPage />} />
      <Route path='/auth/register' element={<RegisterPage />} />
      <Route path='/auth/invitation' element={<InvitationPage />} />
      <Route path='/auth/email' element={<EmailVerificationPage />} />
      <Route path='/auth/email/verified' element={<EmailVerifiedPage />} />
      <Route path='/auth/activate' element={<ActivatePage />} />
      <Route path='/auth/forgot' element={<ForgotPasswordPage />} />
      <Route path='/auth/reset' element={<ResetPasswordPage />} />
      <Route path='/auth/logout' element={<LogoutPage />} />
      <Route path='/auth/signup' element={<SignupPage />} />
      <Route path='/auth/verify' element={<VerifySignupPage />} />
      <Route path='/auth/onboarding/org' element={<OnboardingOrgPage />} />
      <Route path='/auth/onboarding/invite' element={<OnboardingInvitePage />} />
      <Route path='/auth/onboarding/survey' element={<OnboardingSurveyPage />} />
      <Route path='/auth/callback' element={<CallbackPage />} />
      <Route path='/shared/:projectId' element={<SharedPage />} />
      {isOrgDomain ? (
        <Route path='/' element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path='account' element={<AccountPage />} />
          <Route path='users' element={<OrganizationUsers />} />
          <Route path='settings' element={<OrganizationSettings />} />
          <Route path='subscription' element={<SubscriptionPage />} />
          <Route path='referrals' element={<ReferralsPage />} />
          <Route path='archived-projects' element={<ArchivedProjectsPage />} />
          <Route path='projects/:projectId' element={<ProjectWrapper />}>
            <Route index element={<AttributeTable />} />
            <Route path='upload' element={<ProjectUploadPage />} />
            <Route path='upload/batch/:batchId' element={<BatchPage />} />
            <Route path='edit' element={<ProjectEditPage />} />
            <Route path='layers' element={<ProjectLayersPage />} />
            <Route path='layers/:layerId/attributes' element={<AttributeTable />} />
            <Route path='analyses' element={<AnalysesPage />} />
          </Route>
        </Route>
      ) : (
        <Route index element={<OrganizationsLoaderPage />} />
      )}
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  );
};

export default Router;
